import { FC, useState, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { v4 as uuid } from 'uuid';
import { isModuleActive, isGroupModuleActive } from '../../views/LogisticServices/utils/general.utils';
import {
  IsidebarModule,
  getSimpleSidebarModules,
  getOperationsModule,
  getCatalogModule,
  getFooterModules,
  IExpandableModuleGroup,
  SIDEBAR_SIMPLE_MODULE_NAME
} from './SidebarModules';
import useOnlyDevelopers from '../../state/setOnlyDevelopers';
import Logout from './Logout/LogoutModal';
import ExpandableSidebarModule from './ExpandableSidebarModule/ExpandableSidebarModule';
import './SogaSidebar.scss';

const SogaSidebar: FC<ISogaSideBar> = ({ infoSideBar }: ISogaSideBar) => {
  const { onlyDevelopers, isOnlyDevsLogistics, isOnlyDevsAutomates } = useOnlyDevelopers();
  const { push } = useHistory();

  const [selectedModule, setSelectedModule] = useState(infoSideBar?.position || 'home');
  const [showLogoutModal, setShowLoggoutModal] = useState(false);

  const footerModules: IsidebarModule[] = getFooterModules(selectedModule, onlyDevelopers);

  const simpleSidebarModules: IsidebarModule[] = useMemo(() => {
    const simpleSidebarModulesRaw = getSimpleSidebarModules(selectedModule, onlyDevelopers);
    const dashboardModule = simpleSidebarModulesRaw.find(
      module => module?.name === SIDEBAR_SIMPLE_MODULE_NAME.DASHBOARD
    );
    const otherModules = simpleSidebarModulesRaw.filter(
      module => module?.name !== SIDEBAR_SIMPLE_MODULE_NAME.DASHBOARD && module?.show && isModuleActive(module?.name)
    );
    return dashboardModule ? [dashboardModule, ...otherModules] : otherModules;
  }, [selectedModule, onlyDevelopers]);

  const operationsModule: IExpandableModuleGroup = useMemo(() => {
    const operationsModuleRaw = getOperationsModule(selectedModule, isOnlyDevsAutomates);
    const filteredSubModules = operationsModuleRaw?.subModules?.filter(
      subModule => subModule?.show && isModuleActive(subModule?.name)
    );
    return {
      ...operationsModuleRaw,
      subModules: filteredSubModules
    };
  }, [selectedModule, onlyDevelopers]);

  const catalogModule: IExpandableModuleGroup = useMemo(() => {
    const catalogModuleRaw = getCatalogModule(selectedModule, onlyDevelopers);
    const filteredSubModules = catalogModuleRaw?.subModules?.filter(
      subModule => subModule?.show && isModuleActive(subModule?.name)
    );
    return {
      ...catalogModuleRaw,
      subModules: filteredSubModules
    };
  }, [selectedModule, onlyDevelopers]);

  const openInNewTab = (url: string): void => {
    const newWindow = window.open(url, '_blank', 'noopener,noreferrer');
    if (newWindow) newWindow.opener = null;
  };

  const changeSidebarModule = (sidebarModule: IsidebarModule, isActive: boolean) => {
    const { name, route, externalRoute } = sidebarModule;
    if (!name || selectedModule === name || !isActive) return;
    if (externalRoute) {
      openInNewTab(externalRoute);
      return;
    }
    setSelectedModule(name);
    if (route) push(route);
  };

  const closeLogoutModal = () => setShowLoggoutModal(false);
  const openLogoutModal = () => setShowLoggoutModal(true);
  /**
   * Renderiza un módulo simple del sidebar
   */
  const generateSidebarModuleUI = (sidebarModule: IsidebarModule) => {
    if (!sidebarModule?.show) return null;

    // Logout y opsSupport siempre están activos si se muestran, no requieren permisos
    const isActive =
      sidebarModule?.name === 'logout' || sidebarModule?.name === 'opsSupport'
        ? true
        : isModuleActive(sidebarModule?.name);
    const inactiveStyles = isActive ? '' : 'inactive';
    const selectedStyles = selectedModule === sidebarModule?.name ? 'selectItem' : '';
    const className = `sidebarModuleMenu ${selectedStyles} ${inactiveStyles}`;
    const handleClick = () => changeSidebarModule(sidebarModule, isActive);

    return (
      <div key={uuid()} className={className} onClick={handleClick}>
        <div className="moduleIcon">{sidebarModule?.img}</div>
        <div className="moduleTitle">{sidebarModule?.title}</div>
      </div>
    );
  };

  return (
    <div id="sogaSidebar">
      {showLogoutModal && <Logout closeModalCallback={closeLogoutModal} />}
      <div className="sidebarModulesContainer">
        {/* Home */}
        {generateSidebarModuleUI(simpleSidebarModules[0])}

        {/* Operaciones (expandible) */}
        {operationsModule?.subModules?.length > 0 && (
          <ExpandableSidebarModule
            mainModule={operationsModule.mainModule}
            subModules={operationsModule.subModules}
            selectedModule={selectedModule}
            isActive={isGroupModuleActive(
              'operations',
              operationsModule?.subModules?.map(sub => sub?.name)
            )}
            defaultExpanded
            onModuleClick={module => changeSidebarModule(module, true)}
            onSubModuleClick={subModule => changeSidebarModule(subModule, true)}
          />
        )}

        {/* Catálogo (expandible) */}
        {catalogModule?.subModules?.length > 0 && (
          <ExpandableSidebarModule
            mainModule={catalogModule.mainModule}
            subModules={catalogModule.subModules}
            selectedModule={selectedModule}
            isActive={isGroupModuleActive(
              'catalogGroup',
              catalogModule?.subModules?.map(sub => sub?.name)
            )}
            onModuleClick={module => changeSidebarModule(module, true)}
            onSubModuleClick={subModule => changeSidebarModule(subModule, true)}
          />
        )}

        {/* Resto de módulos simples */}
        {simpleSidebarModules.slice(1).map(item => generateSidebarModuleUI(item))}
      </div>

      <footer id="logout-section" className="logoutContainer">
        <div className="sidebarDivider" />
        {footerModules.map(item => {
          return item.name === 'logout' ? (
            <div key={uuid()} onClick={openLogoutModal}>
              {generateSidebarModuleUI(item)}
            </div>
          ) : (
            generateSidebarModuleUI(item)
          );
        })}
      </footer>
    </div>
  );
};

export default SogaSidebar;

interface IPosition {
  position?: string;
}

interface ISogaSideBar {
  infoSideBar?: IPosition;
}
